import { useState } from "react";
import { Plus, Send, PackageCheck, FileDown, XCircle, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingState, ErrorState, EmptyState, Field, FormError, SubmitButton, StatusBadge } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { api, downloadFile } from "@/lib/api";
import { fmtCents, fmtDate } from "@/lib/format";
import { useSave } from "@/modules/inventory/PartsTab";
import { t } from "@/i18n";

function SupplierDialog({ sup, onClose, onDone }) {
  const [f, setF] = useState(sup ? { name: sup.name, contact: sup.contact || "", email: sup.email || "", phone: sup.phone || "", account_no: sup.account_no || "" } : { name: "", contact: "", email: "", phone: "", account_no: "" });
  const body = { ...f, email: f.email || null };
  const { busy, error, submit } = useSave(() => (sup ? api.put(`/inventory/suppliers/${sup.id}`, body) : api.post("/inventory/suppliers", body)), onDone, t("b8.inv.saved"));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="supplier-dialog">
        <DialogHeader><DialogTitle>{sup ? sup.name : t("b8.inv.newSupplier")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FormError message={error} />
          {["name", "contact", "email", "phone", "account_no"].map((k) => (
            <Field key={k} label={t(`b8.inv.sup.${k}`)} htmlFor={`s-${k}`}><Input id={`s-${k}`} required={k === "name"} type={k === "email" ? "email" : "text"} value={f[k]} onChange={set(k)} data-testid={`supplier-${k}-input`} /></Field>
          ))}
          <SubmitButton busy={busy} className="w-full" data-testid="supplier-save-button">{t("common.save")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SuppliersTab({ canManage }) {
  const { data, error, loading, reload } = useFetch("/inventory/suppliers");
  const [dlg, setDlg] = useState(null);
  return (
    <div className="space-y-3" data-testid="suppliers-tab">
      {canManage && <Button onClick={() => setDlg({})} data-testid="new-supplier-button"><Plus className="mr-1.5 h-4 w-4" />{t("b8.inv.newSupplier")}</Button>}
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState title={t("b8.inv.noSuppliers")} />}
      {data?.map((s) => (
        <div key={s.id} className="gf-card flex items-center gap-3 p-4" data-testid={`supplier-row-${s.id}`}>
          <div className="min-w-0 flex-1"><p className="font-semibold">{s.name}</p><p className="text-xs text-slate-500">{[s.contact, s.email, s.phone, s.account_no && `Acct ${s.account_no}`].filter(Boolean).join(" · ")}</p></div>
          {canManage && <Button variant="ghost" size="icon" onClick={() => setDlg(s)} data-testid={`supplier-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>}
        </div>
      ))}
      {dlg && <SupplierDialog sup={dlg.id ? dlg : null} onClose={() => setDlg(null)} onDone={() => { setDlg(null); reload(); }} />}
    </div>
  );
}

function PODialog({ po, onClose, onDone }) {
  const sups = useFetch("/inventory/suppliers");
  const parts = useFetch("/inventory/parts");
  const [f, setF] = useState(po ? { supplier_id: po.supplier_id, notes: po.notes || "", lines: po.lines.map((l) => ({ part_id: l.part_id, qty: String(l.qty), unit_cost: (l.unit_cost_cents / 100).toFixed(2) })) }
    : { supplier_id: "", notes: "", lines: [{ part_id: "", qty: "1", unit_cost: "" }] });
  const { busy, error, submit } = useSave(() => (po ? api.put(`/inventory/purchase-orders/${po.id}`, f) : api.post("/inventory/purchase-orders", f)), onDone, t("b8.inv.saved"));
  const setLine = (i, patch) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="po-dialog">
        <DialogHeader><DialogTitle>{po ? po.number : t("b8.inv.newPo")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FormError message={error} />
          <Field label={t("b8.inv.supplier")}>
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
              <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder={t("b8.inv.chooseSupplier")} /></SelectTrigger>
              <SelectContent>{(sups.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 items-end gap-2">
              <div className="col-span-6"><Select value={l.part_id} onValueChange={(v) => { const p = parts.data.find((x) => x.id === v); setLine(i, { part_id: v, unit_cost: l.unit_cost || (p.cost_cents / 100).toFixed(2) }); }}>
                <SelectTrigger data-testid={`po-line-${i}-part`}><SelectValue placeholder={t("b8.inv.pick")} /></SelectTrigger>
                <SelectContent>{(parts.data || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
              </Select></div>
              <Input className="col-span-2" type="number" min={0.01} step="any" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`po-line-${i}-qty`} />
              <Input className="col-span-3" type="number" min={0} step="0.01" placeholder={t("b8.inv.cost")} value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })} data-testid={`po-line-${i}-cost`} />
              <Button type="button" variant="ghost" size="icon" className="col-span-1 text-red-600" disabled={f.lines.length === 1} onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, lines: [...f.lines, { part_id: "", qty: "1", unit_cost: "" }] })} data-testid="po-add-line"><Plus className="mr-1 h-4 w-4" />{t("b8.inv.addLine")}</Button>
          <Field label={t("b8.inv.notes")} htmlFor="po-notes"><Input id="po-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="po-notes-input" /></Field>
          <SubmitButton busy={busy} disabled={!f.supplier_id || f.lines.some((l) => !l.part_id)} className="w-full" data-testid="po-save-button">{t("common.save")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ po, onClose, onDone }) {
  const open = po.lines.filter((l) => l.received_qty < l.qty);
  const [qty, setQty] = useState(Object.fromEntries(open.map((l) => [l.id, String(l.qty - l.received_qty)])));
  const lines = open.filter((l) => Number(qty[l.id]) > 0).map((l) => ({ line_id: l.id, qty: qty[l.id] }));
  const { busy, error, submit } = useSave(() => api.post(`/inventory/purchase-orders/${po.id}/receive`, { lines }), onDone, t("b8.inv.received"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="receive-dialog">
        <DialogHeader><DialogTitle>{t("b8.inv.receiveTitle", { number: po.number })}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FormError message={error} />
          {open.map((l) => (
            <div key={l.id} className="flex items-center gap-3 text-sm"><span className="min-w-0 flex-1">{l.sku} — {l.name} <span className="text-slate-500">({l.received_qty}/{l.qty})</span></span>
              <Input type="number" min={0} step="any" className="w-24" value={qty[l.id]} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} data-testid={`receive-qty-${l.sku}`} /></div>
          ))}
          <SubmitButton busy={busy} disabled={!lines.length} className="w-full" data-testid="receive-save-button">{t("b8.inv.receive")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function OrdersTab() {
  const { data, error, loading, reload } = useFetch("/inventory/purchase-orders");
  const [dlg, setDlg] = useState(null);
  const done = () => { setDlg(null); reload(); };
  const act = (po, action) => api.post(`/inventory/purchase-orders/${po.id}/${action}`).then(reload);
  return (
    <div className="space-y-3" data-testid="orders-tab">
      <Button onClick={() => setDlg({ kind: "edit" })} data-testid="new-po-button"><Plus className="mr-1.5 h-4 w-4" />{t("b8.inv.newPo")}</Button>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState title={t("b8.inv.noPos")} />}
      {data?.map((po) => (
        <div key={po.id} className="gf-card flex flex-wrap items-center gap-3 p-4" data-testid={`po-row-${po.number}`}>
          <div className="min-w-0 flex-1"><p className="font-mono text-sm font-semibold">{po.number}</p><p className="text-xs text-slate-500">{po.supplier_name} · {fmtDate(po.created_at)} · {po.lines.length} {t("b8.inv.lines")} · {fmtCents(po.total_cents)}</p></div>
          <StatusBadge status={po.status === "partial" ? "partially_paid" : po.status} testId={`po-status-${po.number}`} />
          <span className="text-xs text-slate-500">{t(`b8.inv.po.${po.status}`)}</span>
          <Button variant="ghost" size="icon" onClick={() => downloadFile(`/inventory/purchase-orders/${po.id}/pdf`)} aria-label="pdf" data-testid={`po-pdf-${po.number}`}><FileDown className="h-4 w-4" /></Button>
          {po.status === "draft" && <Button variant="ghost" size="icon" onClick={() => setDlg({ kind: "edit", po })} data-testid={`po-edit-${po.number}`}><Pencil className="h-4 w-4" /></Button>}
          {["draft", "sent"].includes(po.status) && <Button variant="outline" size="sm" onClick={() => act(po, "send")} data-testid={`po-send-${po.number}`}><Send className="mr-1 h-4 w-4" />{t("b8.inv.send")}</Button>}
          {["draft", "sent", "partial"].includes(po.status) && <Button size="sm" onClick={() => setDlg({ kind: "receive", po })} data-testid={`po-receive-${po.number}`}><PackageCheck className="mr-1 h-4 w-4" />{t("b8.inv.receive")}</Button>}
          {["draft", "sent"].includes(po.status) && <Button variant="ghost" size="icon" className="text-red-600" onClick={() => act(po, "cancel")} data-testid={`po-cancel-${po.number}`}><XCircle className="h-4 w-4" /></Button>}
        </div>
      ))}
      {dlg?.kind === "edit" && <PODialog po={dlg.po} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === "receive" && <ReceiveDialog po={dlg.po} onClose={() => setDlg(null)} onDone={done} />}
    </div>
  );
}
